// src/views/consumo/FotoLeituraButton.jsx
// Botão "📷 Foto" que abre um popover/modal compacto para:
//  - capturar/escolher uma foto do medidor (mobile: usa câmera)
//  - rodar OCR (Tesseract) no backend e sugerir o número
//  - permitir ao usuário aceitar/editar
//
// Quando "onAplicar" é chamado, o componente devolve { leitura, base64, mimeType }
// que pode ser usado pra preencher o input de leitura e (opcionalmente) salvar
// a foto como anexo quando a leitura for confirmada.

import React, { useState, useRef } from "react";
import consumoService from "../../services/consumoService";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default function FotoLeituraButton({ onAplicar, color = "#1D4ED8", compact = true }) {
  const [open, setOpen]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [preview, setPreview] = useState(null);     // dataURL
  const [mimeType, setMime]   = useState(null);
  const [ocr, setOcr]         = useState(null);     // { leitura, confianca, raw }
  const [editado, setEditado] = useState("");
  const [erro, setErro]       = useState("");
  const fileRef = useRef(null);

  const reset = () => {
    setPreview(null); setMime(null); setOcr(null); setEditado(""); setErro("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    setErro(""); setOcr(null); setEditado("");
    if (file.size > 8 * 1024 * 1024) { setErro("Imagem muito grande (máx 8MB)."); return; }
    try {
      const dataUrl = await fileToBase64(file);
      setPreview(dataUrl);
      setMime(file.type || "image/jpeg");
      setBusy(true);
      const r = await consumoService.ocrPreview({ base64: dataUrl, mimeType: file.type });
      setOcr(r.data || null);
      if (r.data?.leitura != null) setEditado(String(r.data.leitura));
    } catch(e) {
      setErro(e.message || "Falha ao processar imagem.");
    } finally { setBusy(false); }
  };

  const aplicar = () => {
    const val = parseFloat(String(editado).replace(",","."));
    if (Number.isNaN(val)) { setErro("Informe um valor numérico."); return; }
    onAplicar?.({
      leitura: val,
      base64:  preview,
      mimeType,
      leituraOCR: ocr?.leitura,
      confiancaOCR: ocr?.confianca,
    });
    setOpen(false); reset();
  };

  return (
    <>
      <button type="button"
        title="Capturar foto da leitura"
        onClick={()=>setOpen(true)}
        style={{
          border:"1px solid var(--gray-300)", background:"var(--bg-surface)", borderRadius:6,
          padding: compact ? "3px 8px" : "6px 12px", cursor:"pointer", color, fontSize: 12,
        }}>
        <i className="bi bi-camera"/>
      </button>

      {open && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:1080,
          display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={(e)=>{ if (e.target === e.currentTarget) { setOpen(false); reset(); } }}>
          <div style={{background:"var(--bg-surface)",borderRadius:12,maxWidth:480,width:"100%",
            padding:18, boxShadow:"0 10px 25px rgba(0,0,0,.2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <h5 style={{margin:0,fontSize:16,color:"var(--gray-700)"}}>
                <i className="bi bi-camera me-2" style={{color}}/>Foto da Leitura
              </h5>
              <button className="btn-close" onClick={()=>{ setOpen(false); reset(); }}/>
            </div>

            {!preview && (
              <>
                <p style={{fontSize:12,color:"var(--gray-500)",marginBottom:10}}>
                  Tire uma foto do mostrador do medidor ou escolha uma imagem.
                  O sistema vai tentar reconhecer o número automaticamente.
                </p>
                <label className="btn btn-outline-primary w-100" style={{cursor:"pointer"}}>
                  <i className="bi bi-camera-fill me-1"/>Tirar foto / escolher
                  <input ref={fileRef} type="file" accept="image/*" capture="environment"
                    style={{display:"none"}} onChange={(e)=>handleFile(e.target.files?.[0])}/>
                </label>
              </>
            )}

            {preview && (
              <>
                <div style={{textAlign:"center",marginBottom:10}}>
                  <img src={preview} alt="preview" style={{maxWidth:"100%",maxHeight:220,borderRadius:8,border:"1px solid var(--border-color)"}}/>
                </div>

                {busy && (
                  <div style={{textAlign:"center",fontSize:13,color:"var(--gray-500)"}}>
                    <span className="spinner-border spinner-border-sm me-2"/>Reconhecendo número (OCR)...
                  </div>
                )}

                {!busy && ocr && (
                  <div style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:8,padding:"10px 12px",marginBottom:10,fontSize:12,color:"#0C4A6E"}}>
                    {ocr.leitura != null ? (
                      <>
                        <div><strong>Sugestão:</strong> <span style={{fontFamily:"monospace",fontSize:14}}>{ocr.leitura}</span></div>
                        <div style={{color:"var(--gray-600)",fontSize:11}}>Confiança: {ocr.confianca}%{ocr.confianca < 70 ? " (revise o número!)" : ""}</div>
                      </>
                    ) : (
                      <div>Não foi possível reconhecer o número automaticamente. Digite manualmente abaixo.</div>
                    )}
                  </div>
                )}

                <label className="form-label" style={{fontSize:12,fontWeight:600}}>Leitura (m³)</label>
                <input type="number" step="0.001" className="form-control"
                  value={editado} onChange={(e)=>setEditado(e.target.value)}
                  placeholder="0.000" autoFocus/>

                {erro && <div className="alert alert-danger py-1 mt-2" style={{fontSize:12}}>{erro}</div>}

                <div style={{display:"flex",gap:8,marginTop:14}}>
                  <button className="btn btn-outline-secondary flex-fill" onClick={reset}>
                    <i className="bi bi-arrow-counterclockwise me-1"/>Outra foto
                  </button>
                  <button className="btn btn-primary flex-fill" onClick={aplicar} disabled={busy || !editado}>
                    <i className="bi bi-check-circle me-1"/>Usar leitura
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
